import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RotateCcw, Settings, ChevronDown, LogOut, User, Shield, AlertTriangle, Wifi, WifiOff, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMarket } from '../contexts/MarketProvider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// SESSION 63: SIMPLIFIED 3-STATE SYSTEM (SSoT DRIVEN)
// ============================================
// The Frontend STOPS "thinking" and STARTS "rendering".
// Only 3 states exist: LIVE, REPLAY, CLOSED
// 
// Rules:
// - Rule #1 (Trump Card): Market Open → LIVE (Admin cannot override)
// - Rule #2 (Admin Override): Market Closed + Replay Active → REPLAY
// - Rule #3 (Default): Market Closed + Replay Inactive → CLOSED
// ============================================

const STATUS_LIVE = 'LIVE';
const STATUS_REPLAY = 'REPLAY';
const STATUS_CLOSED = 'CLOSED';

// Status Badge Component - SESSION 86: WEBSOCKET-DRIVEN STATUS
// Now reads app_mode directly from WebSocket (strategyPulse) instead of polling API
// SESSION 100: SSoT - Status is determined purely by backend app_mode
const StatusBadge = ({ appMode, isSocketConnected }) => {
  // ============================================
  // SESSION 100: SINGLE SOURCE OF TRUTH (SSoT)
  // Backend sends app_mode: LIVE, REPLAY, or CLOSED
  // We trust the backend completely - no client-side logic
  // ============================================
  
  // SESSION 99: Normalize appMode to uppercase for case-insensitive comparison
  const normalizedAppMode = appMode?.toUpperCase?.() || 'CLOSED';
  
  // SESSION 100: SSoT - Map backend app_mode directly to status
  // No complex logic - just trust the backend
  let effectiveStatus;
  if (normalizedAppMode === 'LIVE') {
    effectiveStatus = STATUS_LIVE;
  } else if (normalizedAppMode === 'REPLAY' || normalizedAppMode === 'TD_REPLAY') {
    effectiveStatus = STATUS_REPLAY;
  } else {
    effectiveStatus = STATUS_CLOSED;
  }
  
  const configs = {
    [STATUS_LIVE]: {
      icon: '🟢',
      text: 'LIVE',
      className: 'bg-buy/20 border-buy text-buy',
      animate: true,
      subtitle: 'Market Open - Live Data Stream',
    },
    [STATUS_REPLAY]: {
      icon: '🟠',
      text: 'REPLAY MODE',
      className: 'bg-amber-500/20 border-amber-500 text-amber-400',
      animate: true,
      subtitle: 'Replay Mode - Historical Data Playback',
    },
    [STATUS_CLOSED]: {
      icon: '🔴',
      text: 'MARKET OFFLINE',
      className: 'bg-slate-600/30 border-slate-500 text-slate-400',
      animate: false,
      subtitle: 'Market Closed - Data Frozen',
    },
  };
  // Fallback to CLOSED if unknown status
  const config = configs[effectiveStatus] || configs[STATUS_CLOSED];

  return (
    <div className="flex items-center gap-2">
      <div 
        className={`flex items-center gap-2 px-3 py-1.5 border rounded text-sm font-bold uppercase tracking-wider ${config.className} ${config.animate ? 'animate-pulse' : ''}`}
        data-testid="status-badge"
        title={config.subtitle || config.text}
      >
        <span>{config.icon}</span>
        <span>{config.text}</span>
      </div>
    </div>
  );
};

export const Header = ({ isConnected, onReset, currentSymbol, onSymbolChange, dataMode, marketStatus, isLive, connectionState, isChangingSymbol }) => {
  const { user, isAdmin, devMode, toggleDevMode, logout, token } = useAuth();
  const { activeData } = useMarket();  // SESSION 86: Get strategyPulse for app_mode
  const navigate = useNavigate();
  
  const [symbols, setSymbols] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTelegramSettings, setShowTelegramSettings] = useState(false);
  const [telegramPaid, setTelegramPaid] = useState(false);
  const [telegramChannelLink, setTelegramChannelLink] = useState('');
  const [showTelegramBanner, setShowTelegramBanner] = useState(false);
  const userMenuRef = useRef(null);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
        setShowTelegramSettings(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryInfo, setExpiryInfo] = useState(null);
  const [lastKnownPrice, setLastKnownPrice] = useState(0);
  const [hftPaused, setHftPaused] = useState(false);
  const [localMarketStatus, setLocalMarketStatus] = useState(null);
  
  // ============================================
  // SESSION 100: SINGLE SOURCE OF TRUTH FOR APP_MODE
  // ============================================
  // The ONLY source is: strategyPulse.app_mode from WebSocket
  // - Backend determines mode based on: market_status + DB flag
  // - Frontend trusts backend completely, no local overrides
  // ============================================
  const wsAppMode = activeData?.strategyPulse?.app_mode;
  
  // SESSION 100: Use WebSocket app_mode ONLY - no API fallback that can conflict
  // Default to CLOSED when no WebSocket data (market offline, not connected)
  let appMode = wsAppMode || 'CLOSED';
  
  // Normalize TD_REPLAY to REPLAY (TrueData's replay mode indicator)
  if (appMode === 'TD_REPLAY') {
    appMode = 'REPLAY';
  }

  // Fetch feed status on mount AND periodically (for badge fallback)
  // SESSION 100: Removed /api/feed/status polling - WebSocket is the SSoT
  // Polling was causing conflicts with WebSocket app_mode
  // localMarketStatus is only used for initial load and replay window info (non-critical)
  useEffect(() => {
    // Fetch once on mount for initial state and replay window info
    fetch(`${BACKEND_URL}/api/feed/status`)
      .then(res => res.json())
      .then(data => {
        setLocalMarketStatus(data);
      })
      .catch(console.error);
    // No polling interval - WebSocket is the real-time source

    // Fetch Telegram settings on mount
    if (token && user) {
      fetch(`${BACKEND_URL}/api/auth/telegram-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setTelegramPaid(data.is_paid || false);
            setTelegramChannelLink(data.channel_link || '');
            if (data.is_paid && !data.channel_link && !localStorage.getItem('telegram_banner_dismissed')) {
              setShowTelegramBanner(true);
            }
          }
        })
        .catch(() => {});
    }

  }, []);

  // Update state from connectionState prop - using derived values instead of effects
  const derivedLastKnownPrice = connectionState?.last_price || lastKnownPrice;
  const derivedHftPaused = connectionState?.hft_paused || false;

  // Fetch available symbols
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/symbols`)
      .then(res => res.json())
      .then(data => {
        if (data.symbols) {
          setSymbols(data.symbols);
        }
      })
      .catch(console.error);
  }, []);

  // Fetch expiry date when symbol changes (Smart Expiry Selector)
  useEffect(() => {
    if (currentSymbol) {
      // Use the new Smart Expiry endpoint
      fetch(`${BACKEND_URL}/api/expiry/active/${currentSymbol}`)
        .then(res => res.json())
        .then(data => {
          if (data.expiry_date_display) {
            setExpiryDate(data.expiry_date_display);
            // Store full expiry info for tooltips
            setExpiryInfo(data);
          } else if (data.error) {
            // Fallback to old endpoint
            fetch(`${BACKEND_URL}/api/admin/expiry/${currentSymbol}`)
              .then(res => res.json())
              .then(fallbackData => {
                if (fallbackData.next_expiry) {
                  const date = new Date(fallbackData.next_expiry);
                  const formatted = date.toLocaleDateString('en-IN', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  }).toUpperCase();
                  setExpiryDate(formatted);
                  setExpiryInfo(fallbackData);
                }
              })
              .catch(console.error);
          }
        })
        .catch(console.error);
    }
  }, [currentSymbol]);

  const handleSymbolSelect = async (symbol) => {
    // Close dropdown immediately for responsive UX
    setShowDropdown(false);
    
    if (symbol === currentSymbol) {
      return;
    }
    
    // Call parent handler which handles the API call
    onSymbolChange?.(symbol);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="terminal-header" data-testid="terminal-header">
      {/* Active Symbol Display - Primary focus (Logo removed - kept in Sidebar only) */}
      <div className="flex items-center gap-3">
        {/* Current Symbol - Large & Prominent */}
        <div className={`flex items-center gap-2 px-3 py-1 bg-surface border rounded transition-all duration-200 ${
          isChangingSymbol ? 'border-accent/50 bg-accent/5' : 'border-accent/30'
        }`}>
          {isChangingSymbol ? (
            <>
              <Loader2 className="w-4 h-4 text-accent animate-spin" />
              <span className="font-mono text-sm font-bold text-accent/70">Loading...</span>
            </>
          ) : (
            <>
              <span className="font-mono text-base font-bold text-accent">
                {currentSymbol || 'NIFTY'}
              </span>
              <span className="text-xs text-muted-foreground font-medium">FUT</span>
            </>
          )}
        </div>
        
        {/* Ticker Info - Expiry Display */}
        <div className="ticker-strip hidden md:flex">
          <div className="h-5 w-px bg-border" />
          <div 
            className="ticker-item cursor-help"
            title={expiryInfo ? 
              `${expiryInfo.expiry_cycle} Expiry\n${expiryInfo.status}${expiryInfo.shifted_from_holiday ? '\n⚠️ Shifted from holiday' : ''}` 
              : 'Loading expiry info...'
            }
          >
            <span className="font-mono text-[9px] text-muted-foreground mr-1">EXP:</span>
            <span className={`font-mono text-[10px] font-semibold ${
              expiryInfo?.is_expiry_day 
                ? 'text-sell animate-pulse' 
                : expiryInfo?.days_to_expiry <= 2 
                  ? 'text-warning' 
                  : 'text-accent'
            }`}>
              {expiryDate || 'Loading...'}
            </span>
            {expiryInfo && (
              <span className={`font-mono text-[9px] ml-1 px-1 py-0.5 rounded ${
                expiryInfo.is_expiry_day 
                  ? 'bg-sell/20 text-sell' 
                  : 'bg-muted/30 text-muted-foreground'
              }`}>
                {expiryInfo.is_expiry_day ? 'TODAY!' : `${expiryInfo.days_to_expiry}D`}
              </span>
            )}
            {expiryInfo?.expiry_cycle && (
              <span className="font-mono text-[8px] ml-0.5 text-muted-foreground/70">
                [{expiryInfo.expiry_cycle === 'Weekly' ? 'W' : 'M'}]
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Right side controls - Compact */}
      <div className="flex items-center gap-2">
        {/* Unified Status Badge - SESSION 86: WebSocket-driven status */}
        <StatusBadge appMode={appMode} isSocketConnected={isConnected} />

        {/* SESSION 112: TUNE and ANALYTICS moved to Admin Panel */}

        {/* Reset Button - Compact */}
        <Button
          variant="outline"
          size="sm"
          onClick={onReset}
          className="h-6 px-2 font-mono text-[10px] uppercase tracking-wider hover:bg-accent hover:text-background transition-colors"
          data-testid="reset-session-btn"
        >
          <RotateCcw className="w-3 h-3 mr-1" />
          Reset
        </Button>

        {/* Admin Link (only for admin users) - Compact */}
        {isAdmin && (
          <Link to="/admin">
            <Button
              variant="outline"
              size="sm"
              className="h-6 px-2 font-mono text-[10px] uppercase tracking-wider hover:bg-accent hover:text-background transition-colors"
              data-testid="admin-link-btn"
            >
              <Settings className="w-3 h-3 mr-1" />
              Admin
            </Button>
          </Link>
        )}

        {/* User Menu - Compact */}
        {user && (
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-1.5 px-2 py-1 bg-surface border border-border hover:border-accent transition-colors"
              data-testid="user-menu-btn"
            >
              {isAdmin ? (
                <Shield className="w-3 h-3 text-accent" />
              ) : (
                <User className="w-3 h-3 text-muted-foreground" />
              )}
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {user.email?.split('@')[0]}
              </span>
              <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute top-full right-0 mt-1 bg-surface border border-border shadow-lg z-50 min-w-[200px]">
                {/* User Info */}
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-sm font-medium text-foreground">{user.name || 'User'}</div>
                  <div className="text-xs text-muted-foreground">{user.email}</div>
                  <div className="flex items-center gap-1 mt-1">
                    {isAdmin ? (
                      <span className="px-1.5 py-0.5 text-[10px] bg-accent/20 text-accent rounded">ADMIN</span>
                    ) : (
                      <span className="px-1.5 py-0.5 text-[10px] bg-muted/20 text-muted-foreground rounded">USER</span>
                    )}
                  </div>
                </div>

                {/* Telegram Settings */}
                <button
                  onClick={() => setShowTelegramSettings(!showTelegramSettings)}
                  className="w-full px-3 py-2 text-left hover:bg-accent/20 transition-colors border-b border-border"
                  data-testid="telegram-settings-btn"
                >
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-foreground">Telegram Signals</span>
                    {telegramPaid && (
                      <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-green-500/20 text-green-400 rounded">PAID</span>
                    )}
                  </div>
                </button>

                {showTelegramSettings && (
                  <div className="px-3 py-2 border-b border-border bg-background/50">
                    {telegramPaid && telegramChannelLink ? (
                      <>
                        <div className="text-[11px] text-muted-foreground mb-3">
                          Join our private signal channel to receive real-time trading signals on Telegram.
                        </div>
                        <a
                          href={telegramChannelLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded hover:bg-cyan-500/30 transition-colors"
                          data-testid="telegram-join-btn"
                        >
                          <Send className="w-4 h-4" />
                          Join Signal Channel
                        </a>
                        <div className="text-[10px] text-muted-foreground/60 mt-2">
                          Signals: God Score, IVSA, Alpha Strike
                        </div>
                      </>
                    ) : (
                      <div className="text-[11px] text-muted-foreground">
                        Telegram signals are available for paid subscribers. Contact admin to upgrade your plan.
                      </div>
                    )}
                  </div>
                )}

                {/* Logout */}
                <button
                  onClick={handleLogout}
                  className="w-full px-3 py-2 text-left hover:bg-sell/20 transition-colors flex items-center gap-2 text-sell"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Logout</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Telegram Modal - shown for paid users who haven't joined */}
      {showTelegramBanner && user && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => { setShowTelegramBanner(false); localStorage.setItem('telegram_banner_dismissed', 'true'); }}>
          <div className="bg-surface border border-border rounded-lg shadow-xl p-6 max-w-md mx-4" onClick={e => e.stopPropagation()} data-testid="telegram-banner">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Send className="w-5 h-5 text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Telegram Signals</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Join our private Telegram channel to receive trading signals in real-time. Click "Telegram Signals" in your profile to get started.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowTelegramBanner(false); localStorage.setItem('telegram_banner_dismissed', 'true'); }}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded hover:bg-surface/80"
              >
                Maybe Later
              </button>
              <button
                onClick={() => { setShowUserMenu(true); setShowTelegramSettings(true); setShowTelegramBanner(false); }}
                className="px-4 py-2 text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 rounded hover:bg-cyan-500/30"
              >
                Setup Now
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
